'use strict';


import {
	Box3, Color,
	LinearFilter, LinearMipmapLinearFilter,
	LinearSRGBColorSpace, Matrix4,
	Mesh,
	PlaneGeometry,
	ShaderMaterial,
	TextureLoader, Vector2,
	Vector3,
} from 'three';
import vertex from '../shader/vertex.glsl';
import fragment from '../shader/fragment.glsl';
import { isMobile, resourcesFolder } from '../../../../settings';
import { Cloudflare } from '../../../api/Cloudflare';

const _vec = new Vector3();
const loader = new TextureLoader();

export class Tile {
	constructor (tileSize,x,y,level,side,config) {
		this.tileSize = tileSize;
		this.x = x;
		this.y = y;
		this.side = side;
		this.level = level;
		this.config = config;
		this.loaded = false;
		this.startLoading = false;
		this.bbox = new Box3();
		this.parent = null;
		
		const geometry = new PlaneGeometry(tileSize, tileSize,1,1);
		geometry.scale(-1,1,1);
		const material = new ShaderMaterial({
			polygonOffset: true,
			polygonOffsetUnits: -this.level * 10,
			transparent: true,
			depthWrite: true,
			uniforms: {
				map: {value: null},
				nightMap: {value: null},
				alpha: {value: 0},
				sky: {value: null},
				time: {value: 0},
				buildings: {value: null},
				trees: {value: null },
				panoMatrix: {value: new Matrix4()},
				intersectedColor: {value: new Color()},
				isIntersected: {value: false},
				useSkyMask: {value: false},
				uSpeed: {value: 0.2},
				uTurbulence: {value: 0.004},
				uScale: {value: new Vector2(15,15)},
				uOffsetY: {value: new Vector2(300,150)},
				uOffsetX: {value: new Vector2(100,0)}
			},
			vertexShader: vertex,
			fragmentShader: fragment
		});
		
		this.mesh = new Mesh(geometry,material);
		this.mesh.name = `${this.side}_${this.x}x${this.y}x${this.level}`;
		this.mesh.visible = false;
		this.#setPosition();
	}
	
	setTexture(key,texture) {
		if (this.mesh.material.uniforms.hasOwnProperty(key)) {
			if (key === 'sky') {
				this.mesh.material.uniforms.useSkyMask.value = true;
			}
			this.mesh.material.uniforms[key].value = texture;
		}
	}
	
	setPanoMatrix(mat4) {
		this.mesh.material.uniforms.panoMatrix.value.copy(mat4);
	}
	
	load(tl) {
		if (this.mesh.parent === null) {
			this.parent.add(this.mesh);
			
			if (this.loaded) {
				this.mesh.visible = true;
			}
		}
		
		if (this.loaded || this.startLoading)
			return;
		
		
		this.startLoading = true;
		const origin = `${config.cdnRepository}/${config.projectFolder}/${resourcesFolder}/textures/air`;
		const path = `${this.config.tilesUrl}/${this.level}/${this.side}_${this.x}x${this.y}x${this.level}.jpg?v=${Cloudflare.version}`;
		const textureLoader = tl ? tl : loader;
		
		const texture = textureLoader.load(origin + path,() => {
			this.loaded = true;
			this.mesh.visible = true;
		});
		
		this.#setTextureSettings(texture);
		this.mesh.material.uniforms.map.value = texture;
		
		if (this.config.nightTilesUrl.length && !isMobile) {
			const nightPath = `${this.config.nightTilesUrl}/${this.level}/${this.side}_${this.x}x${this.y}x${this.level}.jpg?v=${Cloudflare.version}`;
			const nightTexture = textureLoader.load(origin + nightPath);
			this.#setTextureSettings(nightTexture);
			this.mesh.material.uniforms.nightMap.value = nightTexture;
		}
	}
	
	#setTextureSettings(texture) {
		texture.colorSpace = LinearSRGBColorSpace;
		texture.anisotropy = 4;
		texture.magFilter = LinearFilter;
		texture.minFilter = LinearMipmapLinearFilter;
	}
	
	dispose() {
		if (this.isRemoved()){
			this.loaded = false;
			this.startLoading = false;
			return;
		}
		
		
		if (this.mesh.parent) {
			this.mesh.removeFromParent();
		}
		
		if (this.mesh.material.uniforms.map.value) {
			this.mesh.material.uniforms.map.value.dispose();
			this.mesh.material.uniforms.map.value = null;
		}
		
		if (this.mesh.material.uniforms.nightMap.value) {
			this.mesh.material.uniforms.nightMap.value.dispose();
			this.mesh.material.uniforms.nightMap.value = null;
		}
		
		this.mesh.geometry.dispose();
		this.mesh.material.dispose();
		this.mesh.visible = false;
		this.loaded = false;
		this.startLoading = false;
	}
	
	updateAlpha(alpha) {
		this.mesh.material.uniforms.alpha.value = alpha;
	}
	
	updateDelta(delta) {
		this.mesh.material.uniforms.time.value = delta;
	}
	
	isRemoved() {
		return !Boolean(this.mesh.parent);
	}
	
	inView(frustum) {
		if (this.bbox.isEmpty()){
			this.mesh.getWorldPosition(_vec);
			this.bbox.setFromCenterAndSize(_vec,new Vector3(this.tileSize,this.tileSize,this.tileSize));
		}
		
		return frustum.intersectsBox(this.bbox);
	}
	
	#setPosition() {
		const offsetX = this.x * this.tileSize;
		const offsetY = this.y * this.tileSize;
		this.mesh.position.x = -(offsetX + this.tileSize / 2 - this.config.size / 2);
		this.mesh.position.y = -(offsetY + this.tileSize / 2 - this.config.size / 2);
	}
}
